import { useField } from "formik";
import React from "react";
import { IControlStyles, useControlStyles } from "../.";
import { ComponentProps, ControlTemplateProps } from "../misc/controlInput";
import { FormatHelper } from "../../../../utils/helpers";
import moment from "moment";
import { ControlDatePicker } from "./controlDatePicker";

export interface ControlDateProps extends ControlTemplateProps {
    useStartDateForMin?: string;
}

export const ControlDate: React.FC<ControlDateProps> = (props) => {
    const styles: IControlStyles = props.controlStyles ?? useControlStyles();
    const [field, meta, helpers] = useField(props.name ?? "");
    const { value: fieldValue } = field;
    const [selectedDate, setSelectedDate] = React.useState<any | undefined>(undefined);

    const minSourceName = props.useStartDateForMin;
    const [minSourceField] = useField(minSourceName ?? "__unused__");

    const explicitMinStr = typeof props.min === "string" ? props.min : undefined;

    const minFromOtherStr = React.useMemo(() => {
        if (!minSourceName) return undefined;

        const raw = minSourceField?.value;
        if (!raw) return undefined;

        const minDate = moment(raw, ["YYYY-MM-DD", moment.ISO_8601], true);
        if (!minDate.isValid()) return undefined;

        if (props.useStartDateForMin) minDate.add(1, "day");

        return minDate.format("YYYY-MM-DD");
    }, [minSourceName, minSourceField?.value, props.useStartDateForMin]);

    const handleDateChange = (value: string) => {
        setSelectedDate(value);
        helpers.setValue(value);

        if (props.onChange) {
            props.onChange({
                target: {
                    name: props.name,
                    value,
                },
            } as React.ChangeEvent<HTMLInputElement>);
        }
    };

    React.useEffect(() => {
        const value: string | undefined = FormatHelper.formatDateForDatePicker(fieldValue ?? props.value) ?? undefined;
        setSelectedDate(value);
    }, [fieldValue, props.value]);

    const computedMin = explicitMinStr && minFromOtherStr
        ? explicitMinStr > minFromOtherStr
            ? explicitMinStr
            : minFromOtherStr
        : explicitMinStr ?? minFromOtherStr;
    const inputProps = ComponentProps(props) as Record<string, any>;
    delete inputProps.type;
    delete inputProps.useStartDateForMin;
    delete inputProps.value;
    delete inputProps.onChange;
    delete inputProps.min;
    delete inputProps.className;
    delete inputProps.style;
    delete inputProps.placeholder;
    delete inputProps.id;
    delete inputProps.name;

    return (
        <>
            <ControlDatePicker
                inputProps={inputProps}
                inputStyle={{ ...(styles.date ?? styles.text ?? styles.input), ...props.style, }}
                placeholder={props.placeholder ?? ""}
                name={props.name}
                value={selectedDate}
                min={computedMin}
                invalid={meta?.touched && !!meta?.error}
                className={[
                    "form-control",
                    "themed-date-input",
                    meta?.error ? "is-invalid" : undefined,
                    props.className,
                ].filter(Boolean).join(" ")}
                onDateChange={handleDateChange}
            />
            {meta?.error && <div className="invalid-feedback">{meta?.error}</div>}
        </>
    );
};
